/**
 * Indicator widgets (charts, KPIs, maps) show EmptyCard instead of ErrorCard
 * when the API fails or returns no data — avoids "Recarregue e tente novamente"
 * for empty/malformed GEO payloads.
 */
export function resolveIndicatorDisplayState({
  isLoading,
  isError,
  isEmpty = false,
  softErrorAsEmpty = true,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty?: boolean;
  softErrorAsEmpty?: boolean;
}) {
  const treatErrorAsEmpty = softErrorAsEmpty && isError && !isLoading;

  return {
    isLoading,
    isError: isError && !treatErrorAsEmpty,
    isEmpty: (isEmpty || treatErrorAsEmpty) && !isLoading,
  };
}

export function isRepairChartDataEmpty(
  data?: { count?: { total?: number } } | null,
): boolean {
  return data != null && (data.count?.total ?? 0) === 0;
}

type FleetCountRecord = Record<string, unknown> | null | undefined;

function getFleetChartTotal(count: FleetCountRecord): number {
  if (!count) return 0;

  const br = count.br as { total?: number } | undefined;
  if (br != null && typeof br.total === 'number') return br.total;

  const total = count.total as { value?: number } | undefined;
  if (total != null && typeof total.value === 'number') return total.value;

  return 0;
}

export function hasFleetRegionChartData(value: unknown): boolean {
  if (value == null) return false;

  if (typeof value === 'object') {
    if ('total' in value && typeof (value as { total?: number }).total === 'number') {
      return ((value as { total: number }).total ?? 0) > 0;
    }

    if ('value' in value) {
      return ((value as { value?: number }).value ?? 0) > 0;
    }
  }

  return false;
}

export function isFleetCountEmpty(count?: FleetCountRecord): boolean {
  if (count == null) return true;

  return getFleetChartTotal(count) === 0;
}

export function isEntranceExitChartEmpty(
  data?: { entrance?: number; exit?: number }[] | null,
): boolean {
  if (!data || data.length === 0) return true;

  return data.every(
    item => (item.entrance ?? 0) === 0 && (item.exit ?? 0) === 0,
  );
}

function getMapCardDataSum(data?: {
  total?: number;
  values?: { label?: string; value?: number }[];
}): number {
  if (!data?.values?.length) return data?.total ?? 0;

  const regionSum = data.values
    .filter(item => item.label !== 'Total')
    .reduce((acc, item) => acc + (item.value ?? 0), 0);

  if (regionSum > 0) return regionSum;

  return (
    data.total ??
    data.values.find(item => item.label === 'Total')?.value ??
    0
  );
}

export function isMapCardDataEmpty(data?: {
  total?: number;
  values?: { label?: string; value?: number }[];
}): boolean {
  return getMapCardDataSum(data) === 0;
}
